import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { GetBaseURL } from "../components/utils/urlUtils";
import {
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  User,
  Mail,
  Building2,
  Phone,
} from "lucide-react";

const API = (GetBaseURL() || "").replace(/\/+$/, "");

// ─── Date Helpers ─────────────────────────────────────────────────────

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function formatDateStr(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDisplayDate(dateStr: string) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(time: string) {
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKEND_DAYS = [0, 6];

// ─── Component ────────────────────────────────────────────────────────

type Step = "calendar" | "time" | "details" | "confirmed";

export default function BookDemoPage() {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");
  const [step, setStep] = useState<Step>("calendar");

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [firmName, setFirmName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Fetch slots when date is selected
  const fetchSlots = useCallback(async (date: string) => {
    try {
      setLoadingSlots(true);
      setSlots([]);
      const res = await axios.get(`${API}/demo/available-slots?date=${date}`);
      setSlots(res.data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);

  const handleDateClick = (day: number) => {
    const dateStr = formatDateStr(currentYear, currentMonth, day);
    setSelectedDate(dateStr);
    setSelectedTime("");
    setStep("time");
  };

  const handleTimeClick = (time: string) => {
    setSelectedTime(time);
    setStep("details");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo/book`, {
        name,
        email,
        firmName: firmName || undefined,
        phone: phone || undefined,
        date: selectedDate,
        time: selectedTime,
      });
      setStep("confirmed");
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Calendar grid
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const isPastMonth =
    currentYear < today.getFullYear() ||
    (currentYear === today.getFullYear() && currentMonth < today.getMonth());

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#0F5132] mb-4">
            <Calendar className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            Book a Free Demo
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
            Get a personalized walkthrough of Kavoa with one of our team members.
            15 minutes, no commitment.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          {/* ── Step: Calendar ────────────────────────────────── */}
          {step === "calendar" && (
            <div className="p-6">
              {/* Month nav */}
              <div className="flex items-center justify-between mb-5">
                <button
                  onClick={prevMonth}
                  disabled={isPastMonth}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-30 transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-base font-bold text-slate-900">
                  {MONTH_NAMES[currentMonth]} {currentYear}
                </h2>
                <button
                  onClick={nextMonth}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAY_HEADERS.map((d) => (
                  <div
                    key={d}
                    className="text-center text-xs font-semibold text-slate-400 py-1"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells before first day */}
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`e-${i}`} />
                ))}

                {/* Days */}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = formatDateStr(currentYear, currentMonth, day);
                  const dayOfWeek = new Date(currentYear, currentMonth, day).getDay();
                  const isWeekend = WEEKEND_DAYS.includes(dayOfWeek);
                  const isPast = dateStr < todayStr;
                  const isToday = dateStr === todayStr;
                  const isSelected = dateStr === selectedDate;
                  const isDisabled = isPast || isWeekend;

                  return (
                    <button
                      key={day}
                      onClick={() => !isDisabled && handleDateClick(day)}
                      disabled={isDisabled}
                      className={`
                        w-full aspect-square rounded-xl text-sm font-medium transition-all
                        flex items-center justify-center
                        ${isSelected
                          ? "bg-[#0F5132] text-white"
                          : isToday
                          ? "bg-emerald-50 text-[#0F5132] font-bold ring-2 ring-emerald-200"
                          : isDisabled
                          ? "text-slate-300 cursor-not-allowed"
                          : "text-slate-700 hover:bg-emerald-50 hover:text-[#0F5132]"
                        }
                      `}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-slate-400 text-center mt-4">
                All times are in Eastern Time (ET)
              </p>
            </div>
          )}

          {/* ── Step: Time Selection ──────────────────────────── */}
          {step === "time" && (
            <div className="p-6">
              <button
                onClick={() => {
                  setStep("calendar");
                  setSelectedDate("");
                }}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>

              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-4 h-4 text-[#0F5132]" />
                <span className="text-sm font-semibold text-slate-900">
                  {formatDisplayDate(selectedDate)}
                </span>
              </div>

              {/* Timezone notice */}
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl mb-5">
                <Clock className="w-5 h-5 text-[#0F5132] flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-slate-800">Eastern Time (ET) — America/Toronto</p>
                  <p className="text-xs text-slate-500">All times shown in US/Canada Eastern (UTC-4)</p>
                </div>
              </div>

              {loadingSlots ? (
                <div className="py-12 text-center">
                  <div className="inline-block w-7 h-7 border-2 border-slate-200 border-t-[#0F5132] rounded-full animate-spin" />
                  <p className="text-sm text-slate-500 mt-3">Loading available times...</p>
                </div>
              ) : slots.length === 0 ? (
                <div className="py-12 text-center">
                  <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">
                    No available times on this date.
                  </p>
                  <button
                    onClick={() => {
                      setStep("calendar");
                      setSelectedDate("");
                    }}
                    className="text-sm text-[#0F5132] font-semibold mt-2 hover:underline"
                  >
                    Pick another date
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {slots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => handleTimeClick(slot)}
                      className={`
                        py-3 rounded-xl text-sm font-semibold transition-all border
                        ${selectedTime === slot
                          ? "bg-[#0F5132] text-white border-[#0F5132]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-[#0F5132] hover:text-[#0F5132]"
                        }
                      `}
                    >
                      {formatTime(slot)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Step: Details Form ────────────────────────────── */}
          {step === "details" && (
            <div className="p-6">
              <button
                onClick={() => setStep("time")}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>

              {/* Selected date/time summary */}
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 mb-6">
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#0F5132]" />
                    <span className="text-sm font-semibold text-slate-900">
                      {formatDisplayDate(selectedDate)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#0F5132]" />
                    <span className="text-sm font-semibold text-slate-900">
                      {formatTime(selectedTime)}
                    </span>
                  </div>
                </div>
                <p className="text-xs font-bold text-emerald-700">Eastern Time (ET) — America/Toronto</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Smith"
                      required
                      className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 transition-all"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="john@lawfirm.com"
                      required
                      className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 transition-all"
                    />
                  </div>
                </div>

                {/* Firm Name */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Firm Name{" "}
                    <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={firmName}
                      onChange={(e) => setFirmName(e.target.value)}
                      placeholder="Smith & Associates"
                      className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 transition-all"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Phone{" "}
                    <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/10 transition-all"
                    />
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-xl bg-[#0F5132] hover:bg-[#146C43] text-white font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Booking..." : "Confirm Booking"}
                </button>
              </form>
            </div>
          )}

          {/* ── Step: Confirmed ───────────────────────────────── */}
          {step === "confirmed" && (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-5">
                <CheckCircle2 className="w-8 h-8 text-[#0F5132]" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                You're all set!
              </h2>
              <p className="text-sm text-slate-500 mb-6 max-w-xs mx-auto">
                Your demo is booked for{" "}
                <strong className="text-slate-900">
                  {formatDisplayDate(selectedDate)}
                </strong>{" "}
                at{" "}
                <strong className="text-slate-900">
                  {formatTime(selectedTime)} ET
                </strong>
                . We've sent a confirmation to your email.
              </p>

              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 mb-6 text-left">
                <p className="text-xs text-slate-500 mb-1 font-semibold uppercase">
                  What to expect
                </p>
                <ul className="text-sm text-slate-700 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    A team member will email you a meeting link
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    15-minute personalized walkthrough
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    No commitment or credit card required
                  </li>
                </ul>
              </div>

              {/* Add to Calendar */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Add to your calendar</p>
                <div className="flex flex-col gap-2">
                  {(() => {
                    const [h, m] = selectedTime.split(":").map(Number);
                    const dateObj = new Date(selectedDate + "T12:00:00");
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
                    const day = String(dateObj.getDate()).padStart(2, "0");
                    const startH = String(h).padStart(2, "0");
                    const startM = String(m).padStart(2, "0");
                    const endH = String(h + (m + 30 >= 60 ? 1 : 0)).padStart(2, "0");
                    const endM = String((m + 30) % 60).padStart(2, "0");
                    const gcalStart = `${year}${month}${day}T${startH}${startM}00`;
                    const gcalEnd = `${year}${month}${day}T${endH}${endM}00`;
                    const title = encodeURIComponent("Kavoa Demo");
                    const details = encodeURIComponent("15-minute personalized walkthrough of Kavoa — practice management for modern law firms.");
                    const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${gcalStart}/${gcalEnd}&ctz=America/New_York&details=${details}`;
                    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${year}-${month}-${day}T${startH}:${startM}:00&enddt=${year}-${month}-${day}T${endH}:${endM}:00&body=${details}`;
                    const icsContent = [
                      "BEGIN:VCALENDAR",
                      "VERSION:2.0",
                      "BEGIN:VEVENT",
                      `DTSTART;TZID=America/New_York:${year}${month}${day}T${startH}${startM}00`,
                      `DTEND;TZID=America/New_York:${year}${month}${day}T${endH}${endM}00`,
                      "SUMMARY:Kavoa Demo",
                      "DESCRIPTION:15-minute personalized walkthrough of Kavoa.",
                      "END:VEVENT",
                      "END:VCALENDAR",
                    ].join("\r\n");
                    const icsBlob = new Blob([icsContent], { type: "text/calendar" });
                    const icsUrl = URL.createObjectURL(icsBlob);

                    return (
                      <>
                        <a
                          href={gcalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 transition-colors"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M18 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="2"/><path d="M16 2v4M8 2v4M4 10h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                          Google Calendar
                        </a>
                        <a
                          href={outlookUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 transition-colors"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="2"/><path d="M4 10h16M10 4v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                          Outlook Calendar
                        </a>
                        <a
                          href={icsUrl}
                          download="kavoa-demo.ics"
                          className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700 transition-colors"
                        >
                          <Calendar className="w-4 h-4" />
                          Download .ics (Apple Calendar)
                        </a>
                      </>
                    );
                  })()}
                </div>
              </div>

              <a
                href="/"
                className="text-sm text-[#0F5132] font-semibold hover:underline"
              >
                Back to Kavoa
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          &copy; {new Date().getFullYear()} Kavoa — Practice Management for
          Modern Law Firms
        </p>
      </div>
    </div>
  );
}
